import * as React from 'react';
import { Box } from '@mui/system';
import { Typography } from '@mui/material';
import { AIMessage, BaseMessage, HumanMessage, ToolMessage, mapStoredMessagesToChatMessages } from '@langchain/core/messages';
import { AgentChatWithMessages } from 'bloggers-backend/lib/aiagents/domain';
import { MessageView } from './MessageView';
import { ToolActivityGroup, ToolActivityEntry } from './ToolActivityGroup';
import { contentText, isToolRejection, parseToolResult } from './messageContent';
import { Bubble } from './Bubble';
import { PendingBubble, ErrorBubble } from './StatusBubbles';
import { ApprovalRequest } from './ApprovalRequest';

type RenderItem = { kind: 'message'; message: BaseMessage } | { kind: 'tools'; entries: ToolActivityEntry[] };

function groupMessages(messages: BaseMessage[]): RenderItem[] {
  const items: RenderItem[] = [];
  let group: ToolActivityEntry[] | null = null;
  const byId = new Map<string, ToolActivityEntry>();

  const flush = () => {
    if (group && group.length > 0) {
      items.push({ kind: 'tools', entries: group });
    }
    group = null;
    byId.clear();
  };

  messages.forEach((message) => {
    if (AIMessage.isInstance(message)) {
      if (contentText(message)) {
        flush();
        items.push({ kind: 'message', message });
      }
      const toolCalls = message.tool_calls ?? [];
      if (toolCalls.length > 0) {
        if (!group) {
          group = [];
        }
        toolCalls.forEach((call, index) => {
          const entry: ToolActivityEntry = { id: call.id ?? `${items.length}-${index}`, name: call.name, args: call.args };
          group?.push(entry);
          if (call.id) {
            byId.set(call.id, entry);
          }
        });
      }
      return;
    }

    if (ToolMessage.isInstance(message)) {
      const content = contentText(message);
      const rejected = isToolRejection(content);
      const result = parseToolResult(content);
      const payload = result.success ? result.value : { reason: result.reason, errors: result.errors };
      const existing = message.tool_call_id ? byId.get(message.tool_call_id) : undefined;
      if (existing) {
        existing.rejected = rejected;
        existing.failed = !rejected && !result.success;
        existing.result = rejected ? undefined : payload;
        return;
      }
      if (!group) {
        group = [];
      }
      group.push({
        id: message.tool_call_id ?? `${items.length}`,
        name: message.name ?? 'tool',
        args: undefined,
        rejected,
        failed: !rejected && !result.success,
        result: rejected ? undefined : payload,
      });
      return;
    }

    if (HumanMessage.isInstance(message)) {
      flush();
      items.push({ kind: 'message', message });
    }
  });

  flush();
  return items;
}

interface ChatConversationProps {
  chat?: AgentChatWithMessages;
  chatsCacheName?: string;
  pendingInput?: string | null;
  onPendingConsumed?: () => void;
}

function humanText(message: BaseMessage): string {
  return typeof message.content === 'string' ? message.content : '';
}

export default function ChatConversation({
  chat,
  chatsCacheName,
  pendingInput,
  onPendingConsumed,
}: ChatConversationProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const prevPendingRef = React.useRef<string | null>(null);

  const messages = React.useMemo(
    () => (chat ? mapStoredMessagesToChatMessages(chat.messages) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chat?.messages],
  );

  const lastHumanText = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (HumanMessage.isInstance(messages[i])) {
        return humanText(messages[i]);
      }
    }
    return undefined;
  }, [messages]);
  const showPending = !!pendingInput && lastHumanText !== pendingInput;

  React.useEffect(() => {
    if (pendingInput && lastHumanText === pendingInput) {
      onPendingConsumed?.();
    }
  }, [pendingInput, lastHumanText, onPendingConsumed]);

  // Switching chats always jumps to the newest message.
  React.useEffect(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight });
  }, [chat?.id]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const justSent = !!pendingInput && prevPendingRef.current !== pendingInput;
    prevPendingRef.current = pendingInput ?? null;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
    if (justSent || nearBottom) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, [chat?.messages.length, chat?.status, pendingInput, showPending]);

  if (!chat && !pendingInput) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">Start a new conversation.</Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
      {groupMessages(messages).map((item, index) =>
        item.kind === 'tools' ? (
          <ToolActivityGroup key={`tools-${index}`} entries={item.entries} />
        ) : (
          <MessageView key={item.message.id ?? `message-${index}`} message={item.message} />
        ),
      )}
      {showPending && (
        <Bubble isUser>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{pendingInput}</Typography>
        </Bubble>
      )}
      {(chat?.status === 'in progress' || (!chat && !!pendingInput)) && <PendingBubble />}
      {chat?.status === 'error' && <ErrorBubble error={chat.error} />}
      {chat?.status === 'interrupted' && chat.interrupt && (
        <ApprovalRequest chatId={chat.id} interrupt={chat.interrupt} chatsCacheName={chatsCacheName} />
      )}
    </Box>
  );
}
